import React, { useEffect, useMemo, useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import * as XLSX from "xlsx";
import { useToast } from "../../../context/ToastContext.jsx";
import { FaTimes } from "react-icons/fa";

const MAX_CONVERSATION_IDS = 5000;
const MAX_CUSTOMER_IDS = 1000;

async function parseCallDownloadUpload(file, filterMode) {
  const requiredHeader = filterMode === "conversation_ids" ? "ID" : "CUSTOMER ID";
  const maxCount = filterMode === "conversation_ids" ? MAX_CONVERSATION_IDS : MAX_CUSTOMER_IDS;
  const name = (file.name || "").toLowerCase();
  let raw = [];
  if (name.endsWith(".txt")) {
    const text = await file.text();
    raw = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  } else {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    if (!rows.length) throw new Error("Empty file.");
    const header = rows[0].map((c) => String(c).trim());
    const idx = header.findIndex((h) => h.toLowerCase() === requiredHeader.toLowerCase());
    if (idx === -1) {
      throw new Error(`The first row must include a "${requiredHeader}" column header.`);
    }
    for (let r = 1; r < rows.length; r++) {
      const v = String(rows[r][idx] ?? "").trim();
      if (v) raw.push(v);
    }
  }
  const seen = new Set();
  const ids = [];
  for (const id of raw) {
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  if (!ids.length) throw new Error("No IDs found in file.");
  if (ids.length > maxCount) {
    throw new Error(`Maximum ${maxCount} IDs allowed (found ${ids.length}).`);
  }
  return ids;
}

const INITIAL_PARAMS = {
  metadata: true,
  transcript: false,
  explanation: false,
  subparameters: false,
  modules: {
    summary: false,
    agentFeedback: false,
    businessInsights: false,
    conversationDispositions: false,
    concernsAndQueries: false,
    leadQuality: false,
    templates: false,
    customModule: false,
  },
};

export default function CallDownloadParametersModal({ isOpen, onClose, onApply, onPreviewRequest, quickFilterApproxCount, filterMode, setFilterMode, setConversationIds, setCustomerIds, quickOnly = false }) {
  const { showToast } = useToast();
  const [selectedParams, setSelectedParams] = useState(INITIAL_PARAMS);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [parsedIds, setParsedIds] = useState([]);
  const [previewTotal, setPreviewTotal] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedParams(INITIAL_PARAMS);
    setShowAdvancedOptions(false);
    setUploadFile(null);
    setParsedIds([]);
    setPreviewTotal(null);
    setPreviewLoading(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && quickOnly && filterMode !== "quick") setFilterMode("quick");
  }, [isOpen, quickOnly, filterMode, setFilterMode]);

  const areAllModulesSelected = useMemo(
    () => Object.values(selectedParams.modules).every(Boolean),
    [selectedParams.modules]
  );

  if (!isOpen) return null;

  const toggleModuleItem = (key) => {
    setSelectedParams((prev) => ({
      ...prev,
      modules: { ...prev.modules, [key]: !prev.modules[key] },
    }));
  };

  const setAllModules = (checked) => {
    setSelectedParams((prev) => ({
      ...prev,
      modules: Object.keys(prev.modules).reduce((acc, k) => {
        acc[k] = checked;
        return acc;
      }, {}),
    }));
  };

  const buildParametersList = () => {
    const list = [];
    list.push("Meta Data");
    if (selectedParams.transcript) list.push("Transcript");
    if (selectedParams.modules.summary) list.push("Summary");
    if (selectedParams.modules.agentFeedback) list.push("Agent Feedback");
    if (selectedParams.modules.businessInsights) list.push("Business Insights");
    if (selectedParams.modules.conversationDispositions) list.push("Conversation Dispositions");
    if (selectedParams.modules.concernsAndQueries) list.push("Concerns and Queries");
    if (selectedParams.modules.leadQuality) list.push("Lead Quality");
    if (selectedParams.modules.templates) list.push("Templates");
    if (selectedParams.modules.customModule) list.push("Custom Module");
    if (selectedParams.explanation) list.push("Explanation");
    if (selectedParams.subparameters) list.push("Subparameters");
    return list;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    setUploadFile(file || null);
    setParsedIds([]);
    setPreviewTotal(null);
    if (!file) return;
    try {
      const ids = await parseCallDownloadUpload(file, filterMode);
      setParsedIds(ids);
      if (filterMode === "conversation_ids") {
        setConversationIds(ids);
        setCustomerIds([]);
      } else if (filterMode === "customer_ids") {
        setCustomerIds(ids);
        setConversationIds([]);
      }
      showToast(`Loaded ${ids.length} ID(s) from file.`, "success");
    } catch (err) {
      showToast(err?.message || "Could not read file.", "error");
    }
    e.target.value = "";
  };

  const handleGetCount = async () => {
    if (filterMode === "quick") {
      setPreviewTotal(typeof quickFilterApproxCount === "number" ? quickFilterApproxCount : null);
      return;
    }
    if (!parsedIds.length) {
      showToast("Upload a file with IDs first.", "error");
      return;
    }
    setPreviewLoading(true);
    setPreviewTotal(null);
    try {
      const total = await onPreviewRequest({
        filterMode,
        conversationIds: filterMode === "conversation_ids" ? parsedIds : [],
        customerIds: filterMode === "customer_ids" ? parsedIds : [],
      });
      setPreviewTotal(total);
      if (total == null) {
        showToast("Could not get count from server. Check file or try again.", "error");
      }
    } catch (_) {
      showToast("Could not get count from server.", "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    const parametersJson = JSON.stringify(buildParametersList());
    if (filterMode === "quick") {
      await onApply({
        parametersJson,
        filterMode: "quick",
        previewTotal: typeof quickFilterApproxCount === "number" ? quickFilterApproxCount : null,
      });
      return;
    }
    if (!parsedIds.length) {
      showToast("Upload a file with IDs first.", "error");
      return;
    }
    await onApply({
      parametersJson,
      filterMode,
      parsedIds: parsedIds
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-lg w-full pt-6 pb-8 px-8 max-h-[95vh] overflow-y-auto" style={{maxWidth: "60rem"}}>
        <div className="mb-4 flex justify-between items-center">
          <div>
          <h3 className="text-lg font-semibold text-gray-900">Download conversations</h3>
          <p className="text-sm text-gray-500">Choose how rows are selected, then pick which fields to include.</p>
          </div>
          <div>
            <button className="text-sm text-gray-500" onClick={onClose}><FaTimes style={{color: "slategray", cursor: "pointer"}}/></button>
          </div>
        </div>
        <div className="h-[70vh] overflow-y-auto">

        <div className="mb-5 rounded-md border border-gray-200 p-3 bg-gray-50">
          <div className="text-sm font-medium text-gray-800 mb-2">Filtering type</div>
          <div className=" text-sm text-gray-700 flex justify-between">
            <label className="flex items-start gap-2 cursor-pointer w-full">
              <input
                type="radio"
                name="call-dl-filter"
                className="mt-1"
                checked={filterMode === "quick"}
                onChange={() => {
                  setFilterMode("quick");
                  setUploadFile(null);
                  setParsedIds([]);
                  setPreviewTotal(null);
                  setConversationIds([]);
                  setCustomerIds([]);
                }}
              />
              <span>
                <span className="font-medium text-gray-900">Quick filters</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                Toolbar and advanced filters will be applied for selection.
                </span>
              </span>
            </label>
            {!quickOnly ? <label className="flex items-start gap-2 cursor-pointer w-full">
              <input
                type="radio"
                name="call-dl-filter"
                className="mt-1"
                checked={filterMode === "conversation_ids"}
                onChange={() => {
                  setFilterMode("conversation_ids");
                  setUploadFile(null);
                  setParsedIds([]);
                  setPreviewTotal(null);
                  setCustomerIds([]);
                }}
              />
              <span>
                <span className="font-medium text-gray-900">Upload conversation IDs</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                Upload a CSV with one conversation ID per row. Filters are ignored. 
                <br/>
                Column header: ID, Limit: 5,000 conversations
                </span>
              </span>
            </label> : null}
            {!quickOnly ? <label className="flex items-start gap-2 cursor-pointer w-full">
              <input
                type="radio"
                name="call-dl-filter"
                className="mt-1"
                checked={filterMode === "customer_ids"}
                onChange={() => {
                  setFilterMode("customer_ids");
                  setUploadFile(null);
                  setParsedIds([]);
                  setPreviewTotal(null);
                  setConversationIds([]);
                }}
              />
              <span>
                <span className="font-medium text-gray-900">Upload customer IDs</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                Upload a CSV with one customer ID per row. Filters are ignored. 
                <br/>
                Column header: CUSTOMER ID, Limit: 1,000 customers.
                </span>
              </span>
            </label> : null}
          </div>

          {filterMode !== "quick" && (
            <div className="mt-3 space-y-2">
              <input
                type="file"
                accept=".csv,.txt,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
                className="block w-full text-xs text-gray-600"
              />
              {uploadFile && <p className="text-xs text-gray-600">File: {uploadFile.name}</p>}
              {parsedIds.length > 0 && <p className="text-xs text-gray-600">{parsedIds.length} ID(s) loaded.</p>}
              <div className="flex flex-wrap items-center gap-2">
              </div>
            </div>
          )}
        </div>

        <div className="mb-2">
          <h4 className="text-sm font-semibold text-gray-900">Choose data to include</h4>
          <p className="text-xs text-gray-500">Meta Data is always included.</p>
        </div>
        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          <div className="flex items-center">
            <input id="dl-metadata" type="checkbox" checked disabled readOnly className="mr-2" />
            <label htmlFor="dl-metadata" className="text-sm font-medium text-gray-700">
              Meta Data
            </label>
          </div>
          <div className="border rounded-md p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700">Modules</div>
              <button type="button" className="text-xs text-indigo-600" onClick={() => setAllModules(!areAllModulesSelected)}>
                {areAllModulesSelected ? "Unselect all" : "Select all"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" checked={selectedParams.modules.summary} onChange={() => toggleModuleItem("summary")} />
                Summary
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" checked={selectedParams.modules.agentFeedback} onChange={() => toggleModuleItem("agentFeedback")} />
                Agent Feedback
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" checked={selectedParams.modules.businessInsights} onChange={() => toggleModuleItem("businessInsights")} />
                Business Insights
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" checked={selectedParams.modules.conversationDispositions} onChange={() => toggleModuleItem("conversationDispositions")} />
                Conversation Dispositions
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" checked={selectedParams.modules.concernsAndQueries} onChange={() => toggleModuleItem("concernsAndQueries")} />
                Concerns and Queries
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" checked={selectedParams.modules.leadQuality} onChange={() => toggleModuleItem("leadQuality")} />
                Lead Quality
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" checked={selectedParams.modules.templates} onChange={() => toggleModuleItem("templates")} />
                Templates
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" checked={selectedParams.modules.customModule} onChange={() => toggleModuleItem("customModule")} />
                Custom Module
              </label>
            </div>
          </div>
          <div className="h-[150px] overflow-y-auto">
            <div className="flex items-center justify-between w-[30%] mb-2">
              <div className="text-sm font-medium text-gray-700">Advanced Options</div>
              <button type="button" className="text-xs" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}>
                {showAdvancedOptions ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              </button>
            </div>
            {showAdvancedOptions && (
              <>
                <div className="flex items-center mb-2">
                  <input
                    id="dl-explanation"
                    type="checkbox"
                    checked={selectedParams.explanation}
                    onChange={(e) => setSelectedParams((prev) => ({ ...prev, explanation: e.target.checked }))}
                    className="mr-2"
                  />
                  <label htmlFor="dl-explanation" className="text-sm">
                    Explanation
                  </label>
                </div>
                <div className="flex items-center mb-2">
                  <input
                    id="dl-subparameters"
                    type="checkbox"
                    checked={selectedParams.subparameters}
                    onChange={(e) => setSelectedParams((prev) => ({ ...prev, subparameters: e.target.checked }))}
                    className="mr-2"
                  />
                  <label htmlFor="dl-subparameters" className="text-sm">
                    Subparameters
                  </label>
                </div>
                <div className="flex items-center mb-2">
                  <input
                    id="dl-transcript"
                    type="checkbox"
                    checked={selectedParams.transcript}
                    onChange={(e) => setSelectedParams((prev) => ({ ...prev, transcript: e.target.checked }))}
                    className="mr-2"
                  />
                  <label htmlFor="dl-transcript" className="text-sm">
                    Transcript
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button type="button" className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="px-4 py-2 text-sm rounded text-white" style={{ backgroundColor: "rgb(79, 70, 229)" }} onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
